import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid2";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ProductImageGallery from "../../components/general/products/productImageGallery";
import ProductInfoHeader from "../../components/general/products/productInfoHeader";
import ProductColorSelector from "../../components/general/products/productColorSelector";
import ProductPriceActions from "../../components/general/products/productPriceActions";
import ProductDescriptionCard from "../../components/general/products/productDescriptionCard";
import ProductSpecsCard from "../../components/general/products/productSpecsCard";
import AddedToCartSnackbar from "../../components/general/cart/addedToCartSnackbar";
import ProductShareRow from "../../components/general/products/productShareRow";
import ProductCarousel from "../../components/general/products/productCarousel/productCarousel";

import useUserData from "../../hooks/user/useUserData";
import useProduct from "../../hooks/useProduct";
import {PAGE_LOGIN, PRODUCTS_LIST} from "../../locales/pages/namespaces";
import { mockProducts } from "./mock/mockProducts";
import CircularProgress from "@mui/material/CircularProgress";
import useCartAction from "../../hooks/cart/useCartAction";
import useCartData from "../../hooks/cart/useCartData";
import {enqueueSnackbar} from "notistack";



const TOKENS = {
    contentMax: 1335,
    topHeight: 544,
    radiusLg: 4,
    gapPx: 30,
    gray: "#9E9E9E",
    panelTopBg: "#F0F0F0",
    panelBg: "#FFFFFF",
    stroke: "#E9E9E9",
};

const CONTAINER_W = {
    width: "95vw",
    //maxWidth: 1720,
    mx: "auto",
};

function Panel({ children, sx = {}, className, noPadding = false }) {
    return (
        <Paper
            elevation={0}
            className={className}
            sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "hidden",
                bgcolor: "#E9E9E9",
                borderColor: TOKENS.panelTopBg,
                borderRadius: TOKENS.radiusLg,
                p: noPadding ? 0 : { xs: 2, md: 3 },
                "& img": { maxWidth: "100%", height: "auto" },
                ...sx,
            }}
        >
            {children}
        </Paper>
    );
}

export default function SingleProductPage() {
    const { id } = useParams();
    const history = useHistory();
    const { isLoggedIn, language } = useUserData();
    const { t } = useTranslation(PRODUCTS_LIST);
    const [qty, setQty] = useState(1);
    const [activeImage, setActiveImage] = useState(0);
    const { loading, product } = useProduct({id});
    const{editCart,setCartMessage} = useCartAction()
    const { message } = useCartData();

    useEffect(() => {
        if (!message) return;

        const text =
            typeof message.get === "function" ? message.get("text") : message?.text;
        const variant =
            typeof message.get === "function"
                ? message.get("variant")
                : message?.variant || "error";

        if (!text) return;

        enqueueSnackbar(t("Failed to update cart!"), {variant:"error",});

        setCartMessage(null);
    }, [message, setCartMessage, t]);

    useEffect(() => {
        if (!product?.defaultColorId) return;
        setActiveImage(0);
        setQty(1);
    }, [product?.defaultColorId, product?.id]);

    const productName = useMemo(() => {
        if (!product) return "";
        return language === "el"
            ? (product.nameEL ?? product.nameEN ?? "")
            : (product.nameEN ?? product.nameEL ?? "");
    }, [product, language]);

    const handlePrint = useCallback(() => {
        window.print();
    }, []);

    const handleShareFacebook = useCallback(() => {
        const url = encodeURIComponent(window.location.href);
        window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            "_blank",
            "noopener,noreferrer"
        );
    }, []);

    const handleShareInstagram = useCallback(() => {
        const url = encodeURIComponent(window.location.href);
        const text = encodeURIComponent(productName);

        window.open(
            `https://instagram.com`,
            "_blank",
            "noopener,noreferrer"
        );
    }, [productName]);

    const handleAddToCart = async () => {
        if (!product?.id) return;

        const imageSrc =
            typeof product.images?.[0] === "string"
                ? product.images[0]
                : product.images?.[0]?.src || product.images?.[0]?.image || "";

        const numericPrice =
            Number(String(product.price?.current ?? product.price ?? "0").replace(",", ".")) || 0;

        const productTitle =
            language === "el"
                ? (product.nameEL ?? product.nameEn ?? product.nameEN ?? "")
                : (product.nameEN ?? product.nameEn ?? product.nameEL ?? "");

        try {
            await editCart({
                id: String(product.id),
                quantity: qty,
                suppressNotification: false,
                product: {
                    id: String(product.id),
                    title: productTitle,
                    image: imageSrc,
                    price: numericPrice,
                    quantity: qty,
                    code: product.code || "",
                },
            });

        } catch (err) {
            console.error(err);
        }
    };

    const handleChangeColor = useCallback(
        (id) => {
            history.push(`/product/${id}`);
        },
        [history]
    );

    if (loading) {
        return <Box sx={{ p: 4 }}>
            <CircularProgress />
        </Box>;
    }

    if (!product) {
        return <Box sx={{ p: 4 }}>{t("Product is not available")}</Box>;
    }

    return (
        <Box sx={{ py: { xs: 0, md: 2, lg: 4 } }}>
            <Box sx={CONTAINER_W}>
                <Panel noPadding sx={{ p: { xs: 2, md: 3 } }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "minmax(380px,1fr) minmax(420px,1fr)",
                                lg: "minmax(560px,1fr) minmax(520px,1fr)",
                            },
                            columnGap: { xs: 2, md: 3, lg: 4 },
                            alignItems: "start",
                        }}
                    >
                        <Box
                            sx={{
                                minWidth: 0,
                                justifySelf: "stretch",
                                alignSelf: "start",
                                aspectRatio: "4 / 3",
                                maxHeight: { xs: 360, md: 420, lg: 480 },
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <ProductImageGallery
                                images={product.images ?? []}
                                currentIndex={activeImage}
                                setActiveImage={setActiveImage}
                            />
                        </Box>

                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                minWidth: 0,
                                justifySelf: "stretch",
                                alignSelf: { xs: "stretch", lg: "start" },
                                display: "grid",
                                gridAutoRows: "min-content",
                                ml: { md: 4, lg: 6 },
                            }}
                        >
                            <Stack
                                spacing={{ xs: 1.5, md: 2 }}
                                sx={{
                                    minWidth: 0,
                                    "& > *": { minWidth: 0 },
                                    "& .MuiTypography-root": {
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                        hyphens: "auto",
                                    },
                                }}
                            >
                                <ProductInfoHeader product={product} />

                                <ProductColorSelector
                                    colors={product.colors ?? []}
                                    product={product}
                                    value={product.id}
                                    onChange={handleChangeColor}
                                />

                                <Divider sx={{ borderColor: TOKENS.stroke }} />

                                {isLoggedIn && (
                                    <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                                        <ProductPriceActions
                                            price={{
                                                current: product.price?.current ?? "",
                                                old: product.price?.old ?? "",
                                                currency: product.price?.currency ?? "€",
                                                vatLabel: t("without VAT"),
                                            }}
                                            product={product}
                                            quantity={qty}
                                            onQuantityChange={setQty}
                                            onInc={() => setQty((q) => q + 1)}
                                            onDec={() => setQty((q) => Math.max(1, q - 1))}
                                            onAdd={handleAddToCart}
                                            below={
                                                <ProductShareRow
                                                    onPrint={handlePrint}
                                                    onShareFacebook={handleShareFacebook}
                                                    onShareInsta={handleShareInstagram}
                                                />
                                            }
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    </Box>
                </Panel>
            </Box>

            <Box
                sx={{
                    ...CONTAINER_W,
                    mt: 2,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                }}
            >
                <Panel sx={{ p: { xs: 2, md: 3 } }}>
                    <ProductDescriptionCard
                        title={t("Description")}
                        text={language === "el" ? product.descriptionEL : product.descriptionEN}
                    />
                </Panel>

                <Panel sx={{ p: { xs: 2, md: 3 } }}>
                    <ProductSpecsCard
                        title={t("Characteristics")}
                        specs={product.specs ?? []}
                    />
                </Panel>
            </Box>

            {/*<Box sx={{ ...CONTAINER_W, mt: 2 }}>*/}
            {/*    <Grid item xs={12}>*/}
            {/*        <Panel sx={{ p: { xs: 2, md: 3 } }}>*/}
            {/*            <ProductCarousel*/}
            {/*                title={t("See what others who viewed this product clicked next!")}*/}
            {/*                products={mockProducts.recommendations ?? []}*/}
            {/*                allProducts={true}*/}
            {/*                onSeeAll={() => history.push("/products")}*/}
            {/*            />*/}
            {/*        </Panel>*/}
            {/*    </Grid>*/}
            {/*</Box>*/}
        </Box>
    );
}